import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { formatDate } from '../../i18n';
import { useCustomerList } from '../crm/queries';
import { useMasterData } from '../masterdata/queries';
import type { ArticleAction, ArticleStatus, ArticleVisibility } from './api';
import type { ArticleDraft } from './ArticlePane';
import { CustomerPicker } from './CustomerPicker';
import { ARTICLE_VISIBILITIES } from './presentation';
import { RatingStars } from './RatingStars';
import { useKbCategories, useRateArticle } from './queries';
import { Select } from '../../components/Select';
import { MultiCombobox } from '../../components/MultiCombobox';

interface ArticleFormProps {
  draft: ArticleDraft;
  onPatch: (teil: Partial<ArticleDraft>) => void;
  editing: boolean;
  hasArticle: boolean;
  status: ArticleStatus | null;
  canPublish: boolean;
  actionBusy: boolean;
  onAction: (action: ArticleAction) => void;
  articleId: string | null;
  ratingAverage: number;
  ratingCount: number;
}

/**
 * Die Eigenschaften eines Artikels -- Ordnung, Sichtbarkeit, Freigabe,
 * Bewertung.
 *
 * **Sie stehen neben dem Text und nicht darunter.** Das ist der Unterschied
 * zwischen einer Maske, die man ueberblickt, und einer, die man abrollt: Wer
 * die Sichtbarkeit auf "Portal" setzt, waehrend er die Durchfuehrung
 * schreibt, sieht beides gleichzeitig -- und "Portal" heisst, dass der Kunde
 * mitliest.
 *
 * **Dieselbe Leiste in beiden Zustaenden.** Beim Lesen eine Aufstellung, beim
 * Bearbeiten dieselben Angaben als Felder, in derselben Reihenfolge, an
 * derselben Stelle. Wer sucht, wo die Kundenzuordnung steht, findet sie
 * zweimal am selben Ort.
 *
 * **Die Aktionen des Freigabelaufs stehen nur da, wenn sie moeglich sind.**
 * Das ist Bequemlichkeit, keine Absicherung: Der Server prueft jeden Uebergang
 * erneut, und ohne ``knowledge.publish`` scheitert die Freigabe dort.
 */
export function ArticleForm({
  draft,
  onPatch,
  editing,
  hasArticle,
  status,
  canPublish,
  actionBusy,
  onAction,
  articleId,
  ratingAverage,
  ratingCount,
}: ArticleFormProps) {
  const { t, i18n } = useTranslation();
  const categories = useKbCategories();
  // Eine Seite genuegt: Wer mehr Kunden hat, als hier hineinpassen, ordnet
  // ueber die Kundenakte zu -- dort ist der Artikel ohnehin vorbelegt.
  const customers = useCustomerList({ limit: 200 });
  // Dieselben Tags wie am Ticket und kein eigener Vorrat -- sonst haette
  // "Exchange" am Vorgang nichts mit "Exchange" am Artikel zu tun.
  const tags = useMasterData('tag');
  const rate = useRateArticle();

  const kundenliste = (customers.data?.pages ?? []).flatMap((page) => page.items);
  const tagListe = (tags.data ?? []).filter((tag) => tag.is_active !== false);

  if (!editing) {
    const kategorie = (categories.data ?? []).find((item) => item.id === draft.categoryId);
    const gewaehlteTags = tagListe.filter((tag) => draft.tagIds.includes(tag.id));

    return (
      <>
        <div className="kb-props__group">
          <h3 className="kb-props__heading">{t('knowledge.properties')}</h3>

          <div className="kb-props__item">
            <span className="kb-props__label">{t('knowledge.category')}</span>
            <span className="kb-props__value">{kategorie?.name ?? t('knowledge.noCategory')}</span>
          </div>

          <div className="kb-props__item">
            <span className="kb-props__label">{t('knowledge.visibility')}</span>
            <span className="kb-props__value">{t(`kbVisibility.${draft.visibility}`)}</span>
          </div>

          <div className="kb-props__item">
            <span className="kb-props__label">{t('knowledge.customers')}</span>
            <span className="kb-props__value">
              {draft.customerIds.length === 0
                ? t('knowledge.customersAll')
                : draft.customerIds
                    .map((id) => kundenliste.find((customer) => customer.id === id)?.name ?? id)
                    .join(', ')}
            </span>
          </div>

          <div className="kb-props__item">
            <span className="kb-props__label">{t('knowledge.tags')}</span>
            <span className="kb-props__value">
              {gewaehlteTags.length === 0 ? (
                t('app.none')
              ) : (
                <span className="row">
                  {gewaehlteTags.map((tag) => (
                    <Badge key={tag.id}>{tag.name}</Badge>
                  ))}
                </span>
              )}
            </span>
          </div>

          <div className="kb-props__item">
            <span className="kb-props__label">{t('knowledge.reviewDue')}</span>
            <span className="kb-props__value">
              {draft.reviewDue
                ? formatDate(draft.reviewDue, i18n.language)
                : t('knowledge.noReviewDue')}
            </span>
          </div>
        </div>

        {hasArticle && status ? (
          <div className="kb-props__group">
            <h3 className="kb-props__heading">{t('knowledge.workflow')}</h3>
            <div className="row">
              {status === 'draft' ? (
                <Button
                  busy={actionBusy}
                  onClick={() => {
                    onAction('submit');
                  }}
                >
                  {t('knowledge.submit')}
                </Button>
              ) : null}
              {status === 'review' && canPublish ? (
                <>
                  <Button
                    variant="primary"
                    busy={actionBusy}
                    onClick={() => {
                      onAction('publish');
                    }}
                  >
                    {t('knowledge.publish')}
                  </Button>
                  <Button
                    busy={actionBusy}
                    onClick={() => {
                      onAction('reject');
                    }}
                  >
                    {t('knowledge.reject')}
                  </Button>
                </>
              ) : null}
              {status !== 'archived' ? (
                <Button
                  variant="ghost"
                  busy={actionBusy}
                  onClick={() => {
                    onAction('archive');
                  }}
                >
                  {t('knowledge.archive')}
                </Button>
              ) : null}
              {status === 'archived' && canPublish ? (
                <Button
                  busy={actionBusy}
                  onClick={() => {
                    onAction('publish');
                  }}
                >
                  {t('knowledge.republish')}
                </Button>
              ) : null}
            </div>
            {status === 'review' && !canPublish ? (
              <span className="field__hint">{t('knowledge.publishNeedsRight')}</span>
            ) : null}
          </div>
        ) : null}

        {hasArticle && articleId ? (
          <div className="kb-props__group">
            <h3 className="kb-props__heading">{t('knowledge.ratingHeading')}</h3>
            <RatingStars
              average={ratingAverage}
              count={ratingCount}
              busy={rate.isPending}
              onRate={(value) => {
                rate.mutate({ id: articleId, rating: value });
              }}
            />
          </div>
        ) : null}
      </>
    );
  }

  return (
    <div className="kb-props__group">
      <h3 className="kb-props__heading">{t('knowledge.properties')}</h3>

      <div className="field">
        <label className="field__label" htmlFor="kb-editor-category">
          {t('knowledge.category')}
        </label>
        <Select
          id="kb-editor-category"
          className="field__control"
          value={draft.categoryId}
          onChange={(event) => {
            onPatch({ categoryId: event.target.value });
          }}
        >
          <option value="">{t('knowledge.noCategory')}</option>
          {(categories.data ?? []).map((category) => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </Select>
      </div>

      <div className="field">
        <label className="field__label" htmlFor="kb-visibility">
          {t('knowledge.visibility')}
        </label>
        <Select
          id="kb-visibility"
          className="field__control"
          value={draft.visibility}
          onChange={(event) => {
            onPatch({ visibility: event.target.value as ArticleVisibility });
          }}
        >
          {ARTICLE_VISIBILITIES.map((value) => (
            <option key={value} value={value}>
              {t(`kbVisibility.${value}`)}
            </option>
          ))}
        </Select>
        <span className="field__hint">{t('knowledge.visibilityHint')}</span>
      </div>

      {/* Gilt fuer -- F4-10, K-113. Keine Auswahl heisst "fuer alle": Das ist
          die Vorgabe und der haeufigere Fall. Wer etwas auswaehlt, schraenkt
          ein -- und im Portal wird daraus eine Grenze, nicht nur eine
          Ordnung. */}
      <div className="field">
        <span className="field__label">{t('knowledge.customers')}</span>
        <CustomerPicker
          options={kundenliste.map((customer) => ({ id: customer.id, name: customer.name }))}
          value={draft.customerIds}
          onChange={(next) => {
            onPatch({ customerIds: next });
          }}
        />
      </div>

      {/* Verschlagwortung -- F4-09, K-25. Dieselben Marken mit Suchfeld wie am
          Ticket (F1-49): Was gewaehlt ist, steht oben; darunter tippt man den
          naechsten Tag. */}
      <div className="field">
        <label className="field__label" htmlFor="article-tags">
          {t('knowledge.tags')}
        </label>
        <MultiCombobox
          id="article-tags"
          value={draft.tagIds}
          placeholder={t('tickets.addTag')}
          options={(tags.data ?? [])
            .filter((tag) => tag.is_active !== false || draft.tagIds.includes(tag.id))
            .map((tag) => ({ id: tag.id, label: tag.name }))}
          onChange={(next) => {
            onPatch({ tagIds: next });
          }}
        />
      </div>

      {/* Ablaufdatum mit Erinnerung -- F4-15, K-110. Ohne Datum erinnert
          nichts; ein Artikel ueber eine Anlage, die es nicht mehr gibt, faellt
          sonst erst dem auf, der danach handelt. */}
      <div className="field">
        <label className="field__label" htmlFor="article-review">
          {t('knowledge.reviewDue')}
        </label>
        <input
          id="article-review"
          type="date"
          className="field__control"
          value={draft.reviewDue}
          onChange={(event) => {
            onPatch({ reviewDue: event.target.value });
          }}
        />
        <span className="field__hint">{t('knowledge.reviewDueHint')}</span>
      </div>

      {hasArticle ? (
        <div className="field">
          <label className="field__label" htmlFor="kb-change-note">
            {t('knowledge.changeNote')}
          </label>
          <input
            id="kb-change-note"
            className="field__control"
            value={draft.changeNote}
            onChange={(event) => {
              onPatch({ changeNote: event.target.value });
            }}
          />
          <span className="field__hint">{t('knowledge.changeNoteHint')}</span>
        </div>
      ) : null}
    </div>
  );
}
